import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";

// Minimal toast system. One stack at the bottom-right. Each toast self-
// dismisses after `durationMs` (default 2.4s). Callers use `useToast()`
// from any component, including non-React contexts via the
// `window.__toast` escape hatch installed below.

export type ToastKind = "info" | "success" | "error";
interface Toast {
  id: number;
  kind: ToastKind;
  message: string;
  durationMs: number;
}

interface ToastApi {
  push: (message: string, opts?: { kind?: ToastKind; durationMs?: number }) => void;
  success: (message: string, durationMs?: number) => void;
  error: (message: string, durationMs?: number) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used inside <ToastProvider>");
  return ctx;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);

  const push = useCallback<ToastApi["push"]>((message, opts) => {
    const id = nextId.current++;
    const t: Toast = {
      id,
      kind: opts?.kind ?? "info",
      message,
      durationMs: opts?.durationMs ?? 2_400,
    };
    setToasts((prev) => [...prev, t]);
    window.setTimeout(() => {
      setToasts((prev) => prev.filter((x) => x.id !== id));
    }, t.durationMs);
  }, []);

  const api: ToastApi = {
    push,
    success: (message, durationMs) => push(message, { kind: "success", durationMs }),
    error: (message, durationMs) => push(message, { kind: "error", durationMs }),
  };

  // Expose a global so non-React code (clipboard handlers in plain JS
  // callbacks, etc.) can fire a toast without prop drilling.
  useEffect(() => {
    (window as unknown as { __toast?: ToastApi }).__toast = api;
    return () => { delete (window as unknown as { __toast?: ToastApi }).__toast; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ToastContext.Provider value={api}>
      {children}
      {createPortal(
        <div className="fixed bottom-4 right-4 z-[200] flex flex-col gap-1.5 pointer-events-none">
          {toasts.map((t) => (
            <div
              key={t.id}
              role="status"
              className="pointer-events-auto rounded-md border border-border bg-surface-2 text-text-dim px-2.5 py-1.5 text-xs flex items-center gap-2 max-w-xs shadow-md opacity-95"
              style={{ animation: "toast-in 140ms ease-out" }}
            >
              <ToastIcon kind={t.kind} />
              <span className="flex-1 min-w-0 break-words">{t.message}</span>
            </div>
          ))}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}

// Copy a string to the clipboard and fire a toast. Returns true on
// success, false if the browser blocked it. Safe to call from any
// non-React callback — uses the `window.__toast` escape hatch wired up
// by ToastProvider.
export async function copyToClipboard(text: string, label = "Copied"): Promise<boolean> {
  const t = (window as unknown as { __toast?: ToastApi }).__toast;
  try {
    await navigator.clipboard.writeText(text);
    t?.success(label);
    return true;
  } catch {
    t?.error("Couldn't copy — clipboard blocked");
    return false;
  }
}

function ToastIcon({ kind }: { kind: ToastKind }) {
  if (kind === "success") {
    return (
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true" className="text-good/70 shrink-0">
        <path d="M5 12l5 5 9-9" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  if (kind === "error") {
    return (
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true" className="text-danger/70 shrink-0">
        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
        <path d="M12 8v5M12 16v.5" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
      </svg>
    );
  }
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true" className="text-text-faint shrink-0">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
      <path d="M12 8h.01M12 11v6" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
    </svg>
  );
}
